/**
 * Singleton QueryClient instance for the AC-1377 React Query migration.
 *
 * This module is the single source of truth for the application-wide
 * `QueryClient`. The provider wired in `src/App.tsx` consumes this exported
 * instance, and any non-test code that needs imperative access to the cache
 * (e.g. invalidating queries from an SSE handler) should import this
 * `queryClient` directly rather than constructing a new one.
 *
 * Defaults follow the Technical Design at
 * `docs/technical/AC-1377-react-query-technical-design.md` (section 2.3):
 *
 *   queries:
 *     staleTime              30_000      (30s - data is "fresh" and not
 *                                          refetched on mount)
 *     gcTime                 300_000     (5m - unused data is garbage
 *                                          collected)
 *     refetchOnWindowFocus   true        (re-validate when user returns to tab)
 *     refetchOnReconnect     true        (re-validate when network recovers)
 *     retry                  1           (one retry on transient errors)
 *
 *   mutations:
 *     retry                  0           (mutations should not silently retry;
 *                                          the caller decides)
 *
 * Tests construct their own short-lived clients via the upcoming
 * `createTestQueryClient` helper rather than reusing this singleton, so test
 * isolation does not depend on resetting state here.
 */

import { QueryClient } from '@tanstack/react-query';

export const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 30_000,
      gcTime: 5 * 60_000,
      refetchOnWindowFocus: true,
      refetchOnReconnect: true,
      retry: 1,
    },
    mutations: {
      retry: 0,
    },
  },
});
